/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import {partition} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import * as OrgActions from 'actions/OrganizationActions';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import {convertToCollaborationCreateOrUpdates, Owner} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {setWindowTitle} from 'BenchlingUtil';
import {
  DataPolicyCollaborationManagementContent,
  NONE_POLICY,
} from 'collaborator-components/CollaborationManagementContent';
import useCollaborationManagementState, {
  Collaboration,
} from 'collaborator-components/useCollaborationManagementState';
import DataConnector from 'connect/DataConnector';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import SourceDAO from 'file-browser/SourceDAO';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import HelpButton from 'help/HelpButton';
import AliasField from 'metadata/AliasField';
import NavStore from 'nav/NavStore';
import notifications from 'notifications';
import {OrganizationPermissionRole} from 'orgs/OrganizationConstants';
import RegistryStore from 'registry/RegistryStore';
import * as RegistryUtil from 'registry/RegistryUtil';
import {
  DataModsItemType,
  getInventoryRegistryChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';
import RegistrySettingsMemberRow from 'settings/registry/RegistrySettingsMemberRow';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import {Header1, Header2} from 'ui-library/Headers';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import licenses from 'user/Licenses';
import * as modals from 'util/Modals';

import DeleteOrArchiveRegistryButtonContainer from './delete-registry/DeleteOrArchiveRegistryButtonContainer';
import RegistryCollaboratorsSection from './RegistryCollaboratorsSection';

const ONLY_LETTERS = /^[a-zA-Z]*$/;

class RegistryGeneralSettingsPageConnector extends DataConnector<
  BaseRegistryGeneralSettingsPageProps,
  EnhancedProps
> {
  entityStore: any;
  navStore: any;
  props: any;
  shouldSubscribeToRedux = false;
  stores = [EntityStore, NavStore, RegistryStore];

  enhanceProps(): EnhancedProps {
    const registry = this.navStore().getSource(this.props.registryId);
    const organization = this.entityStore()
      .currentUser()
      .organizations.find((org: any) => org.url_identifier === this.props.ownerUrlId);
    return {
      isWritable: Boolean(registry) && RegistryUtil.hasSettingsAccess(registry),
      members: organization != null ? this.entityStore().getMembersForGroup(organization.id) : null,
      orgInitials: organization != null ? this.entityStore().getEntity(organization.id).initials : null,
      registry: this.navStore().getSource(this.props.registryId),
    };
  }
}

export interface BaseRegistryGeneralSettingsPageProps {
  ownerUrlId: string;
  registryId: string;
}
interface EnhancedProps {
  isWritable: boolean;
  members?: Array<any>; // TODO: OrganizationConstants.MEMBER.isRequired
  orgInitials?: string;
  registry?: any;
}

class RegistryGeneralSettingsPage extends React.Component<
  BaseRegistryGeneralSettingsPageProps & EnhancedProps
> {
  componentDidMount(): void {
    if (!this.props.members && this._getOrganizationId() != null) {
      OrgActions.fetchMembers(this._getOrganizationId());
    }
    if (!this.props.registry) {
      FileBrowserActions.fetchSources();
    }
  }

  componentWillReceiveProps(nextProps: BaseRegistryGeneralSettingsPageProps): void {
    if (nextProps.ownerUrlId !== this.props.ownerUrlId && this._getOrganizationId() != null) {
      const members = EntityStore.getMembersForGroup(this._getOrganizationId(nextProps.ownerUrlId));
      if (!members) {
        OrgActions.fetchMembers(this._getOrganizationId(nextProps.ownerUrlId));
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _getOrganizationId = (ownerUrlId = this.props.ownerUrlId) => {
    const organization = EntityStore.currentUser().organizations.find(
      (org: any) => org.url_identifier === ownerUrlId
    );
    return organization?.id;
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleSetMemberInitials = (index: any, initials: any) => {
    const initialsByUserId = {[this.props.members![index].id]: initials};
    OrgActions.setInitials(this._getOrganizationId(), {initialsByUserId});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleSetOrgInitials = (newValue: any) => {
    OrgActions.setInitials(this._getOrganizationId(), {orgInitials: newValue, initialsByUserId: {}});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _validateInitials = (initialValue: any, newValue: any) => {
    if (!ONLY_LETTERS.test(newValue)) {
      notifications.error('Initials can only contain letters (no numbers, spaces, or special characters)');
      return false;
    }
    return true;
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _validateOrgInitials = async (initialValue: any, newValue: any) => {
    if (!this._validateInitials(initialValue, newValue)) {
      return false;
    }
    const confirmed = await modals.confirm(
      'Change Organization Initials?',
      `This will change how Registry IDs are generated. We use the schema prefix ` +
        `along with the organization initials (e.g. p${newValue}001).`,
      'danger',
      {promiseCompatible: true}
    );
    return confirmed;
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _validateMemberInitials = (initialValue: any, newValue: any) => {
    if (!this._validateInitials(initialValue, newValue)) {
      return false;
    }
    if (
      initialValue &&
      initialValue.toLowerCase() !== newValue.toLowerCase() &&
      this.props.members!.some(
        (member) => Boolean(member.initials) && member.initials.toLowerCase() === newValue.toLowerCase()
      )
    ) {
      notifications.error('Another member of the organization already has those initials');
      return false;
    }
    return true;
  };

  render(): JSX.Element {
    setWindowTitle('General - Registry Settings');

    const shouldShowPermissions = licenses.hasEnterprisePlan();
    const rows = this.props.members ? (
      this.props.members.map((member, i) => (
        <RegistrySettingsMemberRow
          key={member.handle}
          member={member}
          index={i}
          isRegistryWritable={this.props.isWritable}
          onComplete={this._handleSetMemberInitials}
          validateInitials={this._validateMemberInitials}
        />
      ))
    ) : (
      <div className="u-spacing-top">
        <HorizontalThrobber />
      </div>
    );

    return (
      <div
        className="settingsPage"
        {...getTestProps({component: 'RegistryGeneralSettingsPage', element: 'root'})}
      >
        <div className="settingsPage-contentWrapper">
          <div className={css(styles.header)}>
            <Header1 className="settingsPage-header">General settings</Header1>
            <ExportAuditLogButton
              ids={this.props.registry != null ? [this.props.registry.id] : []}
              buttonSize={ButtonSize.MEDIUM}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
            />
          </div>
          {this.props.registry && shouldShowPermissions && (
            <div>
              <Row className="u-spacing" />
              <Header2>Permissions</Header2>
              {BenchlingConfig.PERMISSIONS_ENABLE_NEW_COLLABORATION_UI ? (
                <RegistryCollaborationManagementContent
                  key={this.props.registryId}
                  registry={this.props.registry}
                  readOnly={!this.props.registry.permissions.admin}
                />
              ) : (
                <RegistryCollaboratorsSection registry={this.props.registry} />
              )}
            </div>
          )}
          {this.props.registry && !this.props.registry.disablePersonalCollections && (
            <div>
              <Row className="u-spacing" />
              <Header2>Collections</Header2>
              <Row>
                <Col xs={4}>
                  <Row className="registrySettingsTable-headerRow">
                    <Col xs={12} className="registrySettingsTable-primaryHeader">
                      Organization Initials
                      <HelpButton
                        iconClassName="u-mini-spacing-left"
                        renderContent={() => (
                          <div className="u-normal-font-weight u-no-text-transform">
                            Used to generate IDs when entities are registered (e.g. "BN" will generate IDs
                            like pBN001)
                          </div>
                        )}
                        placement={FloatingPlacement.BOTTOM}
                      />
                    </Col>
                  </Row>
                  <Row className={classNames('registrySettingsTable-row', 'borderless')}>
                    <Col xs={12} className="registrySettings-field">
                      <AliasField
                        allowEmpty={true}
                        complete={this._handleSetOrgInitials}
                        initialAlias={this.props.orgInitials}
                        isWritable={this.props.isWritable}
                        placeholder="Enter organization initials"
                        validate={this._validateOrgInitials}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="u-spacing" />
              <Row className="registrySettingsTable-headerRow">
                <Col xs={4} className="registrySettingsTable-primaryHeader">
                  Member Username
                </Col>
                <Col xs={4}>Name</Col>
                <Col xs={4}>Initials</Col>
              </Row>
              {rows}
            </div>
          )}
          <Row className="u-spacing" />
          <DeleteOrArchiveRegistryButtonContainer registryId={this.props.registryId} />
        </div>
      </div>
    );
  }
}
export default RegistryGeneralSettingsPageConnector.connect(RegistryGeneralSettingsPage);

const updateRegistryCollaborationsWithDataMods = async (
  registryId: string,
  collaborations: Array<Collaboration>
): Promise<void> => {
  await callAsyncWithDataModifications({
    changeSummary: getInventoryRegistryChangeSummary({
      itemCounts: {[DataModsItemType.REGISTRY]: 1},
      action: InventoryRegistryDataModAction.MODIFY,
    }),
    onPerformAction: (dataModsHeader?: DataModificationsHeader) => {
      const [ownerCollaborations, nonOwnerCollaborations] = partition(
        collaborations,
        (c: Collaboration) => c.fromOwnership ?? false
      );
      const memberOwnerCollaboration = ownerCollaborations
        // Remove the Collaboration with the frontend-only NONE_POLICY so that it does not get sent to the
        // backend.
        .filter((c: Collaboration) => c.policy !== NONE_POLICY)
        .find((c: Collaboration) => c.organizationRole === OrganizationPermissionRole.MEMBER);
      SourceDAO.update(
        registryId,
        {
          collaborations: convertToCollaborationCreateOrUpdates(nonOwnerCollaborations),
          ownerInfo: {
            type: 'organization',
            memberDataPolicyId: memberOwnerCollaboration?.policy?.id ?? null,
          },
        },
        dataModsHeader
      )
        .then(() => {
          notifications.success(`Successfully updated collaborators.`);
        })
        .catch((response: Response) => {
          notifications.notifyError(response);
        });
    },
    apiIds: [registryId],
    analyticsParams: {
      module: 'REGISTRY',
      // @ts-expect-error TODO: Should be using ActionOrigin type
      actionOrigin: 'registry_settings_general_update_collaborators',
      modifiedAction: 'REGISTRY_SETTINGS_COLLABORATORS',
      numModifiedTotalItems: 1,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY,
    // Hard code useDataModsIfEnabledAnywhere to true to
    // enable data mods when project level data mods are enabled
    useDataModsIfEnabledAnywhere: true,
  });
};

export function RegistryCollaborationManagementContent({
  registry,
  readOnly,
}: {
  registry: {
    id: string;
    name: string;
    s_owner: Owner;
  };
  readOnly: boolean;
}): JSX.Element {
  const [collaborationState, collaborationActions] = useCollaborationManagementState(registry.id);
  const [saving, setSaving] = React.useState(false);
  const [canSave, setCanSave] = React.useState<boolean>(true);

  React.useEffect(() => {
    if (collaborationState.queryResultType === QueryResultType.PENDING) {
      LoadingScreen.show();
    } else {
      LoadingScreen.hide();
    }
  }, [collaborationState.queryResultType]);

  if (collaborationState.queryResultType === QueryResultType.ERROR) {
    notifications.error('Could not load collaborators; try reloading the page.');
    return <></>;
  } else if (collaborationState.queryResultType !== QueryResultType.SUCCESS) {
    return <></>;
  }

  const _handleSubmit = async (): Promise<void> => {
    setSaving(true);
    await updateRegistryCollaborationsWithDataMods(registry.id, collaborationState.collaborations)
      .then(async () => {
        await (collaborationActions.refetch?.() ?? Promise.resolve());
      })
      .finally(() => {
        setSaving(false);
      });
  };

  // Unlike other ownables, the registry allows the owner to not have permissions. In that case, the backend
  // will not return a Collaboration for the owner. In the UI we still want to show the owner as the first
  // "Collaboration", so we include a frontend-only Collaboration for this case with the frontend-only NONE
  // policy.
  let collaborations = collaborationState.collaborations;
  if (collaborations.find((c: Collaboration) => c.fromOwnership) === undefined) {
    collaborations = [
      ...collaborations,
      {
        __typename: 'CollaborationNode',
        party: {
          __typename: 'OrganizationNode',
          avatarUrl: registry.s_owner.avatar_url,
          admin: false,
          ...registry.s_owner,
        },
        organizationRole: OrganizationPermissionRole.MEMBER,
        teamRole: null,
        auditor: null,
        fromOwnership: true,
        resource: {
          __typename: 'RestrictedResourceNode',
          id: registry.id,
          name: registry.name,
          type: 'registry',
        },
        policy: NONE_POLICY,
      },
    ];
  }

  return (
    <div>
      <div className="u-large-padding-bottom">
        <DataPolicyCollaborationManagementContent
          {...collaborationState}
          {...collaborationActions}
          collaborations={collaborations}
          isReadOnly={readOnly || saving}
          renderAuditorColumn={false}
          setCanSave={setCanSave}
        />
      </div>
      <div className={css(styles.buttonsDiv)}>
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.MEDIUM}
          text="Save"
          disabled={readOnly || !canSave}
          onClick={async () => {
            await _handleSubmit();
          }}
          dataTestAttrs={{component: 'RegistryCollaborationManagementContent', element: 'saveButton'}}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  buttonsDiv: {
    display: 'flex',
    gap: 5,
    flexDirection: 'row',
  },
});
